import React from 'react';
import { generatePath, Outlet } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { CASHIER_PAYMENT_METHODS_KEYS } from 'api/cashierPaymentMethods/constants';
import { ICashierPaymentMethod } from 'api/cashierPaymentMethods/types';
import { ICONS_MAP } from 'constants/icons';
import { queryClient } from 'queries';
import { normalizeQueryKey } from 'queries/utils';
import { ROUTES } from 'routes/config/constants';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';

import { IRouteParams, IRoutesConfigItem } from '../index';

const CashierSettings = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/CashierSettings'),
);
const CashierLimits = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/CashierLimits'),
);
const CreateLimit = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/MerchantsSettings/CashierLimits/Limit/Create'),
);
const EditLimit = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/CashierLimits/Limit/Edit'),
);
const CashierPaymentMethods = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/PaymentMethods'),
);
const CreateCashierPaymentMethod = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/MerchantsSettings/PaymentMethods/PaymentMethod/Create'
    ),
);
const CreateCashierSettings = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/CashierSettings/Create'),
);
const EditCashierSettings = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/CashierSettings/Edit'),
);
const UpdateCashierPaymentMethod = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/MerchantsSettings/PaymentMethods/PaymentMethod/Edit'
    ),
);

const ViewCashierPaymentMethod = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/MerchantsSettings/PaymentMethods/PaymentMethod/View'
    ),
);

export const MERCHANT_SETTINGS_CHILDREN: IRoutesConfigItem[] = [
  {
    breadcrumb: 'cashierSettings.title',
    children: [
      {
        component: CashierSettings,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_CASHIER_SETTINGS],
      },
      {
        breadcrumb: 'cashierSettings.form.titleCreate',
        component: CreateCashierSettings,
        isPrivate: true,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_CASHIER_SETTING],
        url: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.SUB_PATH.CREATE,
      },
      {
        breadcrumb: 'cashierSettings.form.titleEdit',
        component: EditCashierSettings,
        isPrivate: true,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.SUB_PATH.EDIT.PATH,
        permissions: [PERMISSIONS.CAN_UPDATE_CASHIER_SETTING],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.SUB_PATH.EDIT;

          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.CashierSettings />,
    path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_CASHIER_SETTINGS],
    testId: 'navbarCashierSettings',
    url: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_SETTINGS.PATH,
  },
  {
    breadcrumb: 'cashierLimits.title',
    children: [
      {
        component: CashierLimits,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_LIMITS],
      },
      {
        breadcrumb: 'cashierLimits.form.titleCreate',
        component: CreateLimit,
        isPrivate: true,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.SUB_PATH.CREATE_LIMIT
          .PATH,
        permissions: [PERMISSIONS.CAN_CREATE_CASHIER_PAYMENT_LIMIT],
        url: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.SUB_PATH.CREATE_LIMIT.PATH,
      },
      {
        breadcrumb: 'cashierLimits.form.titleEdit',
        component: EditLimit,
        isPrivate: true,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.SUB_PATH.EDIT_LIMIT.PATH,
        permissions: [PERMISSIONS.CAN_UPDATE_CASHIER_PAYMENT_LIMIT],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.SUB_PATH.EDIT_LIMIT;
          return !id
            ? ''
            : generatePath(PATH, {
                [PARAMS.ID]: id,
              });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.CashierLimits />,
    path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_LIMITS],
    testId: 'navbarCashierLimits',
    url: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.CASHIER_LIMITS.PATH,
  },
  {
    breadcrumb: 'paymentMethods.title',
    children: [
      {
        component: CashierPaymentMethods,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_METHODS],
      },
      {
        breadcrumb: 'paymentMethods.create',
        component: CreateCashierPaymentMethod,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.SUB_PATH.CREATE,
        permissions: [PERMISSIONS.CAN_CREATE_CASHIER_PAYMENT_METHOD],
        url: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.SUB_PATH.CREATE,
      },
      {
        breadcrumb: (params?: IRouteParams): string => {
          if (!params?.id) return '';
          const paymentMethod = queryClient.getQueryData<ICashierPaymentMethod>(
            normalizeQueryKey([CASHIER_PAYMENT_METHODS_KEYS.PAYMENT_METHOD, params.id]),
          );
          return paymentMethod ? paymentMethod.name : 'paymentMethods.paymentMethod';
        },
        component: UpdateCashierPaymentMethod,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.SUB_PATH.EDIT.PATH,
        permissions: [
          PERMISSIONS.CAN_UPDATE_CASHIER_PAYMENT_METHOD,
          PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_METHODS,
        ],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.SUB_PATH.EDIT;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        breadcrumb: (params?: IRouteParams): string => {
          if (!params?.id) return '';
          const paymentMethod = queryClient.getQueryData<ICashierPaymentMethod>(
            normalizeQueryKey([CASHIER_PAYMENT_METHODS_KEYS.PAYMENT_METHOD, params.id]),
          );
          return paymentMethod ? paymentMethod.name : 'paymentMethods.paymentMethod';
        },
        component: ViewCashierPaymentMethod,
        path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.SUB_PATH.VIEW.PATH,
        permissions: [PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_METHODS],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.SUB_PATH.VIEW;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.PaymentMethods />,
    path: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_METHODS],
    testId: 'navbarPaymentMethods',
    url: ROUTES.MERCHANTS_SETTINGS.SUB_PATH.PAYMENT_METHODS.PATH,
  },
];
